import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HotTable } from '@handsontable/react-wrapper';
import { registerAllModules } from 'handsontable/registry';
import { Loader2 } from 'lucide-react';

import 'handsontable/styles/handsontable.css';
import 'handsontable/styles/ht-theme-main.css';
import 'handsontable/styles/ht-theme-horizon.css';
import './HandsontableStyles.css';

registerAllModules();

const SUPPORTED_THEMES = {
    'ht-theme-main': 'ht-theme-main',
    'ht-theme-horizon': 'ht-theme-horizon',
};

/**
 * @param {Array} columns
 * @param {Array} colHeaders
 * @param {Function} onSubmit
 * @param {number} initialEmptyRows
 * @param {string} themeName
 * @param {string} variant
 * @param {string} className
 * @param {Object} handsontableSettings
 */
function HandsontableDataEntry({
    className = '',
    colHeaders,
    columns,
    handsontableSettings = {},
    initialEmptyRows = 10,
    onSubmit,
    themeName = 'ht-theme-horizon',
    variant = 'default',
}) {
    const hotRef = useRef(null);
    const [gridData, setGridData] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [changedRows, setChangedRows] = useState(new Set());

    if (!columns || !Array.isArray(columns) || columns.length === 0) {
        throw new Error('HandsontableDataEntry: columns prop is required and must be a non-empty array');
    }
    if (!colHeaders || !Array.isArray(colHeaders) || colHeaders.length === 0) {
        throw new Error('HandsontableDataEntry: colHeaders prop is required and must be a non-empty array');
    }
    if (!onSubmit || typeof onSubmit !== 'function') {
        throw new Error('HandsontableDataEntry: onSubmit prop is required and must be a function');
    }
    if (columns.length !== colHeaders.length) {
        throw new Error('HandsontableDataEntry: columns and colHeaders arrays must have the same length');
    }

    const validatedThemeName = useMemo(() => {
        if (SUPPORTED_THEMES[themeName]) {
            return SUPPORTED_THEMES[themeName];
        }
        return SUPPORTED_THEMES['ht-theme-horizon'];
    }, [themeName]);

    const generateEmptyRow = useCallback(() => {
        const emptyRow = {};
        columns.forEach((column) => {
            if (column.data) {
                switch (column.type) {
                    case 'numeric':
                        emptyRow[column.data] = null;
                        break;
                    case 'checkbox':
                        emptyRow[column.data] = false;
                        break;
                    case 'dropdown':
                        emptyRow[column.data] = '';
                        break;
                    default:
                        emptyRow[column.data] = '';
                }
            }
        });
        return emptyRow;
    }, [columns]);

    useEffect(() => {
        const initialData = Array.from({ length: initialEmptyRows }, () => generateEmptyRow());
        setGridData(initialData);
    }, [initialEmptyRows, generateEmptyRow]);

    const isRowEmpty = useCallback(
        (rowData) =>
            columns.every((column) => {
                const value = rowData[column.data];
                if (column.type === 'checkbox') {
                    return value === false;
                }
                return value === null || value === undefined || value === '';
            }),
        [columns],
    );

    const effectiveColumns = columns;
    const effectiveColHeaders = colHeaders;

    const handleAfterChange = useCallback((changes, source) => {
        if (!changes || source === 'loadData' || source === 'updateData' || source === 'formatDate') {
            return;
        }

        setGridData((prevData) => {
            const newData = [...prevData];
            changes.forEach(([rowIndex, prop, oldValue, newValue]) => {
                if (newData[rowIndex]) {
                    newData[rowIndex][prop] = newValue;
                }
            });
            return newData;
        });

        const modifiedRows = [...new Set(changes.map((change) => change[0]))];
        setChangedRows((prev) => {
            const newSet = new Set(prev);
            modifiedRows.forEach((rowIndex) => newSet.add(rowIndex));
            return newSet;
        });
    }, []);

    const addRow = useCallback(() => {
        const newRow = generateEmptyRow();
        setGridData((prevData) => {
            const newData = [...prevData, newRow];

            setTimeout(() => {
                if (hotRef.current?.hotInstance) {
                    const newRowIndex = newData.length - 1;
                    hotRef.current.hotInstance.selectCell(newRowIndex, 0);
                }
            }, 100);

            return newData;
        });
    }, [generateEmptyRow]);

    const submitData = useCallback(async () => {
        const nonEmptyRows = gridData.filter((row) => !isRowEmpty(row));

        if (nonEmptyRows.length === 0) {
            return;
        }

        setIsSubmitting(true);
        try {
            await onSubmit(nonEmptyRows);

            setChangedRows(new Set());

            const initialData = Array.from({ length: initialEmptyRows }, () => generateEmptyRow());
            setGridData(initialData);
            if (hotRef.current?.hotInstance) {
                hotRef.current.hotInstance.loadData(initialData);
            }
        } catch (error) {
            // TODO, add error handling
        } finally {
            setIsSubmitting(false);
        }
    }, [gridData, isRowEmpty, onSubmit, initialEmptyRows, generateEmptyRow]);

    const handleDeleteRow = useCallback(
        (rowIndex) => {
            if (rowIndex < 0 || rowIndex >= gridData.length) return;

            setGridData((prevData) => {
                const newData = [...prevData];
                newData.splice(rowIndex, 1);
                return newData;
            });

            setChangedRows((prev) => {
                const newSet = new Set();
                prev.forEach((index) => {
                    if (index < rowIndex) {
                        newSet.add(index);
                    } else if (index > rowIndex) {
                        newSet.add(index - 1);
                    }
                });
                return newSet;
            });
        },
        [gridData.length],
    );

    const duplicateRow = useCallback(
        (row) => {
            if (row < 0 || row >= gridData.length) return;

            const originalRowData = gridData[row];
            const cleanRowData = { ...originalRowData };
            delete cleanRowData._submit;

            setGridData((prevData) => {
                const newData = [...prevData];
                newData.splice(row + 1, 0, cleanRowData);

                setTimeout(() => {
                    if (hotRef.current?.hotInstance) {
                        hotRef.current.hotInstance.selectCell(row + 1, 0);
                    }
                }, 100);

                return newData;
            });
        },
        [gridData],
    );

    const contextMenuConfig = useMemo(
        () => ({
            items: {
                add_row: {
                    name: 'Add New Row',
                    callback: () => addRow(),
                },
                duplicate_row: {
                    name: 'Duplicate Row',
                    callback: (key, selection) => {
                        if (selection && selection.length > 0) {
                            const { row } = selection[0].start;
                            duplicateRow(row);
                        }
                    },
                    disabled: () => gridData.length === 0,
                },
                delete_row: {
                    name: 'Delete Row',
                    callback: (key, selection) => {
                        if (selection && selection.length > 0) {
                            const { row } = selection[0].start;
                            handleDeleteRow(row);
                        }
                    },
                    disabled: () => gridData.length === 0,
                },
            },
        }),
        [addRow, duplicateRow, handleDeleteRow, gridData.length],
    );

    const hasPendingChanges = changedRows.size > 0;
    const nonEmptyChangedRows = Array.from(changedRows).filter(
        (rowIndex) => gridData[rowIndex] && !isRowEmpty(gridData[rowIndex]),
    );

    const effectiveClassName = [
        validatedThemeName,
        className,
        isSubmitting ? 'ht-loading' : '',
        variant !== 'default' ? `ht-variant-${variant}` : '',
    ]
        .filter(Boolean)
        .join(' ');

    const defaultSettings = {
        licenseKey: 'non-commercial-and-evaluation',
        height: 'auto',
        width: 'auto',
        stretchH: 'all',
        autoColumnSize: true,
        rowHeaders: true,
        colHeaders: effectiveColHeaders,
        columns: effectiveColumns,
        manualRowResize: true,
        manualColumnResize: true,
        wordWrap: true,
        manualRowMove: false,
        manualColumnMove: false,
        columnSorting: false,
        dropdownMenu: false,
        filters: false,
        navigableHeaders: true,
        headerClassName: 'htLeft',
        afterChange: handleAfterChange,
        className: effectiveClassName,
        readOnly: isSubmitting,
        minRows: initialEmptyRows,

        ...handsontableSettings,

        contextMenu: contextMenuConfig,
    };

    return (
        <div className="handsontable-wrapper flex flex-col h-full">
            {isSubmitting && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 backdrop-blur-sm">
                    <Loader2 className="w-10 h-10 animate-spin text-gray-600" />
                </div>
            )}

            <div className="flex-1 overflow-hidden">
                <HotTable ref={hotRef} data={gridData} {...defaultSettings} />
            </div>

            <div className="handsontable-actions flex justify-between items-center gap-4 pt-4">
                <div className="handsontable-actions-left">
                    <button
                        type="button"
                        className="ht-button ht-button-add-row"
                        onClick={addRow}
                        disabled={isSubmitting}
                        title="Add new row"
                    >
                        + Add Row
                    </button>
                </div>

                <div className="handsontable-actions-right flex items-center gap-4">
                    {hasPendingChanges && nonEmptyChangedRows.length > 0 && (
                        <span className="ht-pending-changes">
                            {nonEmptyChangedRows.length} row{nonEmptyChangedRows.length !== 1 ? 's' : ''} modified
                        </span>
                    )}
                    <button
                        type="button"
                        className="ht-button ht-button-submit-all"
                        onClick={submitData}
                        disabled={!hasPendingChanges || nonEmptyChangedRows.length === 0 || isSubmitting}
                        title={nonEmptyChangedRows.length === 0 ? 'No changes to submit' : 'Submit all changes'}
                    >
                        {isSubmitting ? 'Submitting...' : 'Submit All'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export { HandsontableDataEntry };
